import { DynamicTexture, PBRMaterial, Texture, type Mesh, type Scene } from "@babylonjs/core";
import { SAILS, SAIL_CELLS, SAIL_COLUMNS, SAIL_ROWS, sailHoles, sailShredded } from "@tiny-seas/shared";

/** DynamicTexture draws on a DOM or offscreen canvas; both 2D contexts carry what the holes need. */
type Canvas2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/**
 * Shot holes and tatters on the ship's sails. A damaged sail gets its own clone of the shared
 * sailcloth material with one canvas texture used twice: its alpha cuts the holes (alpha test)
 * and its colour, as a shadow-mode lightmap, darkens the frayed rims. Everything is drawn in
 * the sail's grid space (metres across from the port leech × metres down from the yard, the
 * `sail_cloth{i}` UVs) from deterministic per-cell jitter, so every client draws the same holes.
 */

const TEXTURE_SIZE = 256;
/** Vertices of a hole's ragged outline. */
export const HOLE_POINTS = 18;
/** Share of the cell's smaller side a hole's mean radius spans. */
const HOLE_RADIUS = [0.27, 0.37] as const;
const RIM_SCALE = 1.2, HALO_SCALE = 1.5;
const CLOTH = "#ffffff", HALO = "#b9ab97", RIM = "#5a4836";
/** Canvas tatters left on the yard when a sail tears away. */
const STRIPS = 8;
const HEM = 0.05;

/** Small deterministic generator (mulberry32) seeded by sail and cell. */
function random(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Sail grid extent in metres: width across the head (UV u) and drop (UV v). */
export function sailGridSize(sail: number): { width: number; drop: number } {
  const s = SAILS[sail]!;
  return { width: 2 * s.half, drop: s.drop };
}

/**
 * Ragged outline of the hole in `cell` of `sail` (grid metres, x from the port leech, y down from
 * the yard), as HOLE_POINTS x/y pairs written into `out`. Pure and deterministic.
 */
export function sailHoleOutline(sail: number, cell: number, out: Float32Array): Float32Array {
  const { width, drop } = sailGridSize(sail);
  const cw = width / SAIL_COLUMNS, ch = drop / SAIL_ROWS;
  const col = cell % SAIL_COLUMNS, row = Math.floor(cell / SAIL_COLUMNS);
  const rand = random(sail * 7919 + cell * 104729 + 17);
  const cx = (col + 0.5 + (rand() - 0.5) * 0.4) * cw, cy = (row + 0.5 + (rand() - 0.5) * 0.4) * ch;
  const radius = Math.min(cw, ch) * (HOLE_RADIUS[0] + (HOLE_RADIUS[1] - HOLE_RADIUS[0]) * rand());
  // A shot tears the cloth along a random grain: stretch the hole along one axis.
  const tear = rand() * Math.PI, stretch = 1 + rand() * 0.45;
  const ct = Math.cos(tear), st = Math.sin(tear);
  for (let k = 0; k < HOLE_POINTS; k++) {
    const a = ((k + (rand() - 0.5) * 0.6) / HOLE_POINTS) * Math.PI * 2;
    const r = radius * (0.62 + 0.55 * rand());
    const lx = Math.cos(a) * r * stretch, ly = Math.sin(a) * r;
    out[k * 2] = cx + lx * ct - ly * st;
    out[k * 2 + 1] = cy + lx * st + ly * ct;
  }
  return out;
}

const outline = new Float32Array(HOLE_POINTS * 2);

/** Traces `outline` scaled by `scale` about its centroid, with every other vertex pushed out by `spike`. */
function trace(ctx: Canvas2D, scale: number, spike: number): void {
  let cx = 0, cy = 0;
  for (let k = 0; k < HOLE_POINTS; k++) { cx += outline[k * 2]!; cy += outline[k * 2 + 1]!; }
  cx /= HOLE_POINTS; cy /= HOLE_POINTS;
  ctx.beginPath();
  for (let k = 0; k < HOLE_POINTS; k++) {
    const s = scale * (k % 2 === 0 ? 1 + spike : 1);
    const x = cx + (outline[k * 2]! - cx) * s, y = cy + (outline[k * 2 + 1]! - cy) * s;
    if (k === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** The hanging strips of a sail torn from its yard, filled in grid metres. */
function drawTatters(ctx: Canvas2D, sail: number, width: number, drop: number): void {
  const rand = random(sail * 15485863 + 3);
  ctx.fillStyle = CLOTH;
  ctx.fillRect(0, 0, width, drop * HEM);
  ctx.strokeStyle = RIM;
  ctx.lineWidth = 0.05;
  const step = width / STRIPS;
  for (let i = 0; i < STRIPS; i++) {
    const gap = step * (0.08 + 0.18 * rand());
    const left = i * step + gap * 0.5, right = (i + 1) * step - gap * 0.5;
    const length = drop * (0.3 + 0.55 * rand());
    ctx.beginPath();
    ctx.moveTo(left, 0);
    // Ragged left edge down to the strip's foot, zigzag tatters across it, ragged right edge back up.
    for (let y = 0.25; y < 1; y += 0.25) ctx.lineTo(left + (rand() - 0.5) * step * 0.18, length * y);
    const teeth = 3 + Math.floor(rand() * 3);
    for (let t = 0; t <= teeth; t++) {
      const x = left + ((right - left) * t) / teeth;
      ctx.lineTo(x, length * (t % 2 === 0 ? 1 - 0.25 * rand() : 0.7 + 0.2 * rand()));
    }
    for (let y = 0.75; y > 0; y -= 0.25) ctx.lineTo(right + (rand() - 0.5) * step * 0.18, length * y);
    ctx.lineTo(right, 0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}

/** Draws sail `sail` of packed `ShipState.sailHoles` into `ctx` (size×size pixels). */
export function drawSailDamage(ctx: Canvas2D, sail: number, packed: number, size: number): void {
  const { width, drop } = sailGridSize(sail);
  ctx.save();
  ctx.setTransform(size / width, 0, 0, size / drop, 0, 0);
  ctx.globalCompositeOperation = "source-over";
  ctx.clearRect(0, 0, width, drop);
  if (sailShredded(packed, sail)) {
    drawTatters(ctx, sail, width, drop);
    ctx.restore();
    return;
  }
  ctx.fillStyle = CLOTH;
  ctx.fillRect(0, 0, width, drop);
  const holes = sailHoles(packed, sail);
  // Scorched halo and frayed rim around every hole first, so neighbouring holes merge into one tear.
  for (const [style, scale, spike] of [[HALO, HALO_SCALE, 0.12], [RIM, RIM_SCALE, 0.22]] as const) {
    ctx.fillStyle = style;
    for (let cell = 0; cell < SAIL_CELLS; cell++) {
      if (!(holes & (1 << cell))) continue;
      sailHoleOutline(sail, cell, outline);
      trace(ctx, scale, spike);
      ctx.fill();
    }
  }
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = "#000000";
  for (let cell = 0; cell < SAIL_CELLS; cell++) {
    if (!(holes & (1 << cell))) continue;
    sailHoleOutline(sail, cell, outline);
    trace(ctx, 1, 0);
    ctx.fill();
  }
  ctx.restore();
}

/** Texture-space mapping (t = uv·scale + offset) that puts the sail grid's port yard corner at 0 and its starboard foot at 1. */
export function sailUvMapping(positions: ArrayLike<number>, normals: ArrayLike<number>, uvs: ArrayLike<number>): { uScale: number; uOffset: number; vScale: number; vOffset: number } {
  // The cloth's two faces carry the grid UVs; the thin rim faces (normals across the sail) carry metric ones.
  let head = -1, foot = -1, headKey = Infinity, footKey = -Infinity;
  for (let i = 0, n = positions.length / 3; i < n; i++) {
    if (Math.abs(normals[i * 3 + 2]!) < 0.5) continue;
    const key = positions[i * 3]! - positions[i * 3 + 1]!;
    if (key < headKey) { headKey = key; head = i; }
    if (key > footKey) { footKey = key; foot = i; }
  }
  if (head < 0 || foot < 0) throw new Error("sail cloth: no grid faces to map holes onto");
  const u0 = uvs[head * 2]!, v0 = uvs[head * 2 + 1]!, u1 = uvs[foot * 2]!, v1 = uvs[foot * 2 + 1]!;
  return { uScale: 1 / (u1 - u0), uOffset: -u0 / (u1 - u0), vScale: 1 / (v1 - v0), vOffset: -v0 / (v1 - v0) };
}

/**
 * One ship sail's damaged presentation: created when the sail is first damaged, disposed once
 * it is whole again (the cloth meshes then return to the shared material).
 */
export class SailCloth {
  private readonly material: PBRMaterial;
  private readonly texture: DynamicTexture;
  private packed = -1;

  constructor(scene: Scene, private readonly sail: number, private readonly meshes: readonly Mesh[], private readonly shared: PBRMaterial) {
    const mesh = meshes[0];
    const positions = mesh?.getVerticesData("position"), normals = mesh?.getVerticesData("normal"), uvs = mesh?.getVerticesData("uv");
    if (!positions || !normals || !uvs) throw new Error(`sail ${sail}: cloth mesh with positions, normals and UVs required`);
    const map = sailUvMapping(positions, normals, uvs);
    this.texture = new DynamicTexture(`sail-damage-${sail}`, { width: TEXTURE_SIZE, height: TEXTURE_SIZE }, scene, true);
    this.texture.hasAlpha = true;
    this.texture.wrapU = this.texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    this.texture.uScale = map.uScale; this.texture.uOffset = map.uOffset;
    this.texture.vScale = map.vScale; this.texture.vOffset = map.vOffset;
    const material = shared.clone(`${shared.name}:sail${sail}-damaged`);
    // The clone copies texture wrappers; share the originals instead.
    for (const slot of ["albedoTexture", "bumpTexture", "metallicTexture"] as const) {
      const copy = material[slot];
      if (copy && copy !== shared[slot]) copy.dispose();
      material[slot] = shared[slot];
    }
    material.opacityTexture = this.texture;
    material.lightmapTexture = this.texture;
    material.useLightmapAsShadowmap = true;
    material.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHATEST;
    material.alphaCutOff = 0.5;
    this.material = material;
    for (const m of meshes) m.material = material;
  }

  /** Redraws when this sail's packed bits change. */
  apply(packed: number): void {
    const bits = sailHoles(packed, this.sail) | (sailShredded(packed, this.sail) ? 1 << SAIL_CELLS : 0);
    if (bits === this.packed) return;
    this.packed = bits;
    drawSailDamage(this.texture.getContext() as unknown as Canvas2D, this.sail, packed, TEXTURE_SIZE);
    this.texture.update(false);
  }

  dispose(): void {
    for (const m of this.meshes) m.material = this.shared;
    this.material.dispose(false, false);
    this.texture.dispose();
  }
}
